import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCompanyProfile, getCompanyRecruiters } from '../services/companyService';

const CompanyProfileDisplay = ({ companyId }) => {
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [recruiters, setRecruiters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        setLoading(true);
        
        // Fetch company profile
        const companyResult = await getCompanyProfile(companyId);
        if (companyResult.success) {
          setCompany(companyResult.data);
        } else {
          setError(companyResult.error);
        }

        // Fetch recruiters
        const recruitersResult = await getCompanyRecruiters(companyId);
        if (recruitersResult.success) {
          setRecruiters(recruitersResult.data);
        }
      } catch (error) {
        setError('Failed to load company profile');
        console.error('Error fetching company data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (companyId) {
      fetchCompanyData();
    }
  }, [companyId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
        {error}
      </div>
    );
  }

  if (!company) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Company profile not found.</p>
      </div>
    );
  }

  const contactDetails = [
    { label: 'Email', value: company.email, type: 'email' },
    { label: 'Phone', value: company.phone, type: 'phone' },
    { label: 'Website', value: company.website, type: 'website' },
    { label: 'Address', value: [company.address, company.city, company.state, company.country, company.zipCode].filter(Boolean).join(', '), type: 'text' }
  ];

  return (
    <div className="max-w-6xl mx-auto">
      {/* Company Header */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Cover Image */}
        <div className="h-48 bg-gradient-to-r from-blue-600 to-purple-600 relative">
          {company.companyImages && company.companyImages.length > 0 && (
            <img
              src={company.companyImages[0]}
              alt="Company cover"
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-black bg-opacity-40"></div>
        </div>

        {/* Company Info */}
        <div className="relative px-6 pb-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between">
            <div className="flex items-end space-x-4 -mt-16">
              {/* Company Logo */}
              <div className="bg-white p-2 rounded-lg shadow-lg">
                {company.logoUrl ? (
                  <img
                    src={company.logoUrl}
                    alt={`${company.companyName} logo`}
                    className="h-24 w-24 object-contain"
                  />
                ) : (
                  <div className="h-24 w-24 bg-gray-200 rounded-lg flex items-center justify-center">
                    <span className="text-2xl font-bold text-gray-400">
                      {company.companyName?.charAt(0)}
                    </span>
                  </div>
                )}
              </div>

              <div className="text-white">
                <h1 className="text-3xl font-bold">{company.companyName}</h1>
                <p className="text-lg opacity-90">{company.industry}</p>
                <div className="flex items-center space-x-4 mt-2 text-sm">
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    {company.location}
                  </span>
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                    </svg>
                    {company.companySize}
                  </span>
                  {company.foundedYear && (
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                      </svg>
                      Founded {company.foundedYear}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3 mt-4 md:mt-0">
              <button 
                onClick={() => navigate('/jobs', { state: { search: company.companyName } })}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                </svg>
                View Jobs
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Company Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-8">
          {/* About */}
          {company.description && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">About {company.companyName}</h2>
              <p className="text-gray-700 leading-relaxed">{company.description}</p>
            </div>
          )}

          {/* Mission & Vision */}
          {(company.mission || company.vision) && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Mission & Vision</h2>
              {company.mission && (
                <div className="mb-4">
                  <h3 className="font-medium text-gray-900 mb-2">Mission</h3>
                  <p className="text-gray-700">{company.mission}</p>
                </div>
              )}
              {company.vision && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Vision</h3>
                  <p className="text-gray-700">{company.vision}</p>
                </div>
              )}
            </div>
          )}

          {/* Company Culture */}
          {(company.culture || company.values || company.benefits) && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Culture & Benefits</h2>
              
              {company.culture && (
                <div className="mb-6">
                  <h3 className="font-medium text-gray-900 mb-2">Company Culture</h3>
                  <p className="text-gray-700">{company.culture}</p>
                </div>
              )}

              {company.values && (
                <div className="mb-6">
                  <h3 className="font-medium text-gray-900 mb-2">Our Values</h3>
                  <p className="text-gray-700">{company.values}</p>
                </div>
              )}

              {company.benefits && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Employee Benefits</h3>
                  <p className="text-gray-700">{company.benefits}</p>
                </div>
              )}
            </div>
          )}

          {/* Company Images */}
          {company.companyImages && company.companyImages.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Company Photos</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {company.companyImages.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`Company photo ${index + 1}`}
                    className="w-full h-32 object-cover rounded-lg"
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Company Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h3>
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-gray-500">Industry</span>
                <p className="text-gray-900">{company.industry}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Company Size</span>
                <p className="text-gray-900">{company.companySize}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Company Type</span>
                <p className="text-gray-900">{company.companyType}</p>
              </div>
              {company.foundedYear && (
                <div>
                  <span className="text-sm font-medium text-gray-500">Founded</span>
                  <p className="text-gray-900">{company.foundedYear}</p>
                </div>
              )}
              <div>
                <span className="text-sm font-medium text-gray-500">Location</span>
                <p className="text-gray-900">{company.location}</p>
              </div>
              {company.website && (
                <div>
                  <span className="text-sm font-medium text-gray-500">Website</span>
                  <a
                    href={company.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    {company.website}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Contact Details */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Details</h3>
            <div className="space-y-3 text-sm">
              {contactDetails.map(({ label, value, type }) => {
                let content = <span className="text-gray-400">Not provided</span>;

                if (value) {
                  if (type === 'email') {
                    content = (
                      <a href={`mailto:${value}`} className="text-blue-600 hover:text-blue-800 break-all">
                        {value}
                      </a>
                    );
                  } else if (type === 'phone') {
                    content = (
                      <a href={`tel:${value}`} className="text-blue-600 hover:text-blue-800">
                        {value}
                      </a>
                    );
                  } else if (type === 'website') {
                    const normalizedUrl = value.startsWith('http') ? value : `https://${value}`;
                    content = (
                      <a
                        href={normalizedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 break-all"
                      >
                        {value}
                      </a>
                    );
                  } else {
                    content = <span className="text-gray-700">{value}</span>;
                  }
                }

                return (
                  <div key={label} className="flex items-start gap-2">
                    <span className="text-gray-500 min-w-[90px]">{label}:</span>
                    {content}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Social Media */}
          {(company.linkedinUrl || company.twitterUrl || company.facebookUrl || company.instagramUrl) && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Follow Us</h3>
              <div className="space-y-3">
                {company.linkedinUrl && (
                  <a
                    href={company.linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-blue-600 hover:text-blue-800"
                  >
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    LinkedIn
                  </a>
                )}
                {company.twitterUrl && (
                  <a
                    href={company.twitterUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-blue-400 hover:text-blue-600"
                  >
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                    </svg>
                    Twitter
                  </a>
                )}
                {company.facebookUrl && (
                  <a
                    href={company.facebookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-blue-600 hover:text-blue-800"
                  >
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                    Facebook
                  </a>
                )}
                {company.instagramUrl && (
                  <a
                    href={company.instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-pink-600 hover:text-pink-800"
                  >
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987s11.987-5.367 11.987-11.987C24.014 5.367 18.647.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C4.198 14.895 3.708 13.744 3.708 12.447s.49-2.448 1.297-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.807.875 1.297 2.026 1.297 3.323s-.49 2.448-1.297 3.323c-.875.807-2.026 1.297-3.323 1.297zm7.718-1.297c-.875.807-2.026 1.297-3.323 1.297s-2.448-.49-3.323-1.297c-.807-.875-1.297-2.026-1.297-3.323s.49-2.448 1.297-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.807.875 1.297 2.026 1.297 3.323s-.49 2.448-1.297 3.323z"/>
                    </svg>
                    Instagram
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Recruiters */}
          {recruiters.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recruitment Team</h3>
              <div className="space-y-4">
                {recruiters.map((recruiter, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-gray-600">
                        {recruiter.name?.charAt(0)}
                      </span>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{recruiter.name}</h4>
                      {recruiter.position && (
                        <p className="text-sm text-gray-600">{recruiter.position}</p>
                      )}
                      {recruiter.department && (
                        <p className="text-sm text-gray-500">{recruiter.department}</p>
                      )}
                      <div className="mt-2 space-y-1 text-xs text-gray-600">
                        {recruiter.email ? (
                          <div>
                            Email: <a href={`mailto:${recruiter.email}`} className="text-blue-600 hover:text-blue-800 break-all">{recruiter.email}</a>
                          </div>
                        ) : (
                          <div>Email: <span className="text-gray-400">Not provided</span></div>
                        )}
                        {recruiter.phone ? (
                          <div>
                            Phone: <a href={`tel:${recruiter.phone}`} className="text-blue-600 hover:text-blue-800">{recruiter.phone}</a>
                          </div>
                        ) : (
                          <div>Phone: <span className="text-gray-400">Not provided</span></div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyProfileDisplay;